import { useState } from "react";
import { Text, TextField } from "@merge-api/merge-javascript-shared";
import { ChevronRight, ListChecks } from "lucide-react";
import MergeTable from "../../shared/MergeTable";
import TableSkeletonLoader from "../../shared/TableSkeletonLoader";
import IntegrationBuilderEmptyState from "../../integration-builder/shared/IntegrationBuilderEmptyState";
import { PlaybookSummary } from "../types";
import { formatRelativeTime } from "../helpers";
import { EnabledBadge } from "./ConductorBadges";

const NUM_COLUMNS = 8;

interface Props {
  playbooks: PlaybookSummary[];
  isLoading: boolean;
  onOpen: (playbookId: string) => void;
}

const ConductorPlaybooksTable = ({ playbooks, isLoading, onOpen }: Props) => {
  const [orgQuery, setOrgQuery] = useState("");
  const [laQuery, setLaQuery] = useState("");

  const filtered = playbooks.filter((pb) => {
    if (orgQuery && !(pb.organization_id || "").toLowerCase().includes(orgQuery.toLowerCase())) {
      return false;
    }
    if (laQuery && !(pb.linked_account_id || "").toLowerCase().includes(laQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <>
      <div className="flex items-center gap-6 mb-6">
        <div className="w-72">
          <TextField
            className="bg-white"
            value={orgQuery}
            onChange={(e) => setOrgQuery(e.target.value)}
            placeholder="Filter by organization ID"
          />
        </div>
        <div className="w-72">
          <TextField
            className="bg-white"
            value={laQuery}
            onChange={(e) => setLaQuery(e.target.value)}
            placeholder="Filter by linked account ID"
          />
        </div>
      </div>

      <MergeTable
        hover={false}
        isOverviewPage
        className="border border-gray-10"
        header={
          <>
            <th scope="col">Playbook ID</th>
            <th scope="col">Category</th>
            <th scope="col">Organization</th>
            <th scope="col">Linked Account</th>
            <th scope="col">Entries</th>
            <th scope="col">Status</th>
            <th scope="col">Updated</th>
            <th scope="col" />
          </>
        }
        content={
          isLoading ? (
            <TableSkeletonLoader cols={NUM_COLUMNS} rows={8} />
          ) : filtered.length === 0 ? (
            <IntegrationBuilderEmptyState
              title="No playbooks"
              subtitle="No playbooks match for this integration."
              numberOfColumns={NUM_COLUMNS}
              withIcon
              icon={ListChecks}
            />
          ) : (
            filtered.map((pb) => (
              <tr
                key={pb.id}
                onClick={() => onOpen(pb.id)}
                className="cursor-pointer hover:bg-[#F9FAFB]"
              >
                <td>
                  <Text variant="md" className="font-mono">
                    {pb.id}
                  </Text>
                </td>
                <td>
                  <Text variant="md" className="font-mono text-gray-60">
                    {pb.category || <span className="text-gray-40">-</span>}
                  </Text>
                </td>
                <td>
                  {pb.organization_id ? (
                    <>
                      {pb.organization_name && (
                        <Text variant="md" className="font-medium">
                          {pb.organization_name}
                        </Text>
                      )}
                      <Text variant="sm" className="block font-mono text-gray-60">
                        {pb.organization_id}
                      </Text>
                    </>
                  ) : (
                    <Text variant="md" className="text-gray-60">
                      All orgs
                    </Text>
                  )}
                </td>
                <td>
                  {pb.linked_account_id ? (
                    <Text variant="md" className="font-mono">
                      {pb.linked_account_id}
                    </Text>
                  ) : (
                    <Text variant="md" className="text-gray-60">
                      All linked accounts
                    </Text>
                  )}
                </td>
                <td>
                  <Text variant="md" className="font-mono">
                    {pb.entry_count}
                  </Text>
                </td>
                <td>
                  <EnabledBadge enabled={pb.enabled} />
                </td>
                <td>
                  <Text variant="md">{formatRelativeTime(pb.modified_at)}</Text>
                </td>
                <td className="text-gray-60">
                  <ChevronRight size={16} />
                </td>
              </tr>
            ))
          )
        }
      />
    </>
  );
};

export default ConductorPlaybooksTable;
